import React, { useState } from 'react';
import { Tab, useTheme } from '@mui/material';
import Column from 'src/frontend-common/components/Column';
import DashboardPageLayout from 'src/frontend-common/components/DashboardPageLayout';
import { ExportModal } from 'src/components/ExportModal';
import { useScheduleAnalyticsMutation, useScheduleExportMutation } from 'src/redux/services/exports';
import { ExportButton, TabsWrapper } from 'src/style/common';
import DownloadIcon from '@mui/icons-material/Download';
import { beginOfTodayAsUtcDate, endOfTodayAsUtcDate } from 'src/utils/date';
import SnackBarCompleteHandler from 'src/components/SnackBarCompleteHandler';
import ExportListWrapper from 'src/components/ExportListWrapper';
import { Products } from 'src/frontend-common/dto/Types/ProductType';

const ExportPage: React.FC = () => {
  const [productFilter, setProductFilter] = useState<Products | 'all' | undefined>(undefined);
  const [activeTab, setActiveTab] = React.useState(0);
  const [isExportModalOpen, setIsExportModalOpen] = React.useState<boolean>(false);

  const [
    scheduleExport,
    { isError: scheduleExportIsError, isSuccess: scheduleExportIsSuccess, isLoading: scheduleExportIsLoading, error: scheduleExportErrorMessage },
  ] = useScheduleExportMutation();

  const [
    analyticsExport,
    { isError: analyticsExportIsError, isSuccess: analyticsExportIsSuccess, isLoading: analyticsExportIsLoading, error: analyticsExportErrorMessage },
  ] = useScheduleAnalyticsMutation();

  const handleTabChange = (_event: unknown, newValue: number) => {
    setActiveTab(newValue);
    switch (newValue) {
      case 0:
        setProductFilter(undefined);
        break;
      case 1:
        setProductFilter('analytics');
        break;
    }
  };

  const a11yProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  };

  const theme = useTheme();

  const handleExport = (
    appKey: string,
    startDate: Date,
    endDate: Date,
    product: Products | 'all' | undefined,
    isFull?: boolean,
    isAnalytics?: boolean
    // eslint-disable-next-line max-params
  ) => {
    setIsExportModalOpen(false);
    if (isAnalytics) {
      analyticsExport({
        app_key: appKey,
        date_to: endDate,
        date_from: new Date(startDate.valueOf() + Math.abs(startDate.getTimezoneOffset()) * 60 * 1000),
      });
    } else {
      scheduleExport({
        app_key: appKey,
        score_cluster: undefined,
        end_date: endDate,
        start_date: startDate,
        search: null,
        is_full: isFull,
        products: product && product !== 'all' ? [product] : undefined,
      });
    }
  };

  const dateRange = {
    startDate: beginOfTodayAsUtcDate(),
    endDate: endOfTodayAsUtcDate(),
  };

  return (
    <DashboardPageLayout
      title="Downloads"
      aside={
        <ExportButton
          text={scheduleExportIsLoading || analyticsExportIsLoading ? 'Waiting for export' : 'Export'}
          icon={<DownloadIcon />}
          iconcolor="primary"
          buttontype="lefticon"
          onClick={() => setIsExportModalOpen(true)}
          disabled={scheduleExportIsLoading || analyticsExportIsLoading}
          waiting={scheduleExportIsLoading || analyticsExportIsLoading ? 'true' : 'false'}
          data-testid="export-button"
        >
          {scheduleExportIsLoading || analyticsExportIsLoading ? 'Waiting for export' : 'Export'}
        </ExportButton>
      }
    >
      <Column
        style={{
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <TabsWrapper value={activeTab} indicatorColor="primary" onChange={handleTabChange} style={{ marginTop: theme.spacing(0) }} fontregular="true">
          <Tab label="CSV" {...a11yProps(0)} data-testid={`csv-tab-${activeTab}`} style={{ minWidth: 116 }} />
          <Tab label="PDF" {...a11yProps(1)} data-testid={`pdf-tab-${activeTab}`} style={{ minWidth: 135 }} />
        </TabsWrapper>
        <ExportListWrapper productFilter={productFilter} />
      </Column>
      <ExportModal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onSubmit={handleExport}
        setActiveTab={setActiveTab}
        setProductFilter={setProductFilter}
        search=""
        dateRange={dateRange}
        scoreCluster={undefined}
        fullExportSwitchHidden={false}
      />
      <SnackBarCompleteHandler
        isSuccess={scheduleExportIsSuccess}
        isError={scheduleExportIsError}
        error={scheduleExportErrorMessage}
        successMessage="Export scheduled successfully"
      />
      <SnackBarCompleteHandler
        isSuccess={analyticsExportIsSuccess}
        isError={analyticsExportIsError}
        error={analyticsExportErrorMessage}
        successMessage="Export scheduled successfully"
      />
    </DashboardPageLayout>
  );
};

export default ExportPage;
